import React, { useState, useEffect, useRef } from 'react';
import {
  Button, Typography, Container, Dialog, DialogTitle, DialogContent, DialogActions,
  Box, TextField, IconButton, FormControlLabel, Switch, Select, MenuItem,
  FormControl, InputLabel, Slider, Stack,
} from '@mui/material';
import '../styles/Timer.css';
import { PlayArrow, Pause, Refresh, Settings as SettingsIcon, VolumeUp, VolumeOff } from '@mui/icons-material';

const DEFAULT_SETTINGS = {
  pomodoro: 25,
  shortBreak: 5,
  longBreak: 15,
  longBreakInterval: 4,
  autoStartBreaks: false,
  autoStartPomodoros: false,
  soundEnabled: true,
  volume: 0.3,
};

const MODE_META = {
  pomodoro: { label: 'Focus', short: 'Pomodoro', emoji: '🍅', from: '#FF4081', to: '#F50057' },
  'short-break': { label: 'Short Break', short: 'Short Break', emoji: '☕', from: '#00E5FF', to: '#00B8D4' },
  'long-break': { label: 'Long Break', short: 'Long Break', emoji: '🌿', from: '#7C4DFF', to: '#6200EA' },
};

const loadSettings = () => {
  try {
    const saved = JSON.parse(localStorage.getItem('pomodoroSettings'));
    return saved ? { ...DEFAULT_SETTINGS, ...saved } : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
};

const loadActiveTaskId = () => {
  try {
    return JSON.parse(localStorage.getItem('activeTaskId')) ?? null;
  } catch {
    return null;
  }
};

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const durationFor = (type, settings) => {
  const map = { pomodoro: settings.pomodoro, 'short-break': settings.shortBreak, 'long-break': settings.longBreak };
  return Math.max(1, parseInt(map[type], 10) || 1) * 60;
};

// Draws a small progress ring into the favicon so the countdown is visible from another tab.
const updateFavicon = (progress, color) => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.arc(32, 32, 28, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(128,128,128,0.25)';
    ctx.fill();
    if (progress > 0) {
      ctx.beginPath();
      ctx.moveTo(32, 32);
      ctx.arc(32, 32, 28, -Math.PI / 2, -Math.PI / 2 + progress * Math.PI * 2);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
    }
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = canvas.toDataURL('image/png');
  } catch {
    // Canvas favicons aren't supported everywhere; safe to skip.
  }
};

const gradientSx = (from, to, { flex = 1 } = {}) => ({
  flex,
  background: `linear-gradient(135deg, ${from}, ${to})`,
  color: '#fff',
  fontWeight: 'bold',
  boxShadow: `0 6px 20px ${from}99`,
  transition: 'all 0.2s ease',
  '&:hover': { background: `linear-gradient(135deg, ${to}, ${from})`, transform: 'scale(1.03)' },
  '&:active': { transform: 'scale(0.96)' },
});

const RADIUS = 120;
const CIRC = 2 * Math.PI * RADIUS;

const Timer = () => {
  const [settings, setSettings] = useState(loadSettings);
  const [sessionType, setSessionType] = useState('pomodoro');
  const [secondsLeft, setSecondsLeft] = useState(() => durationFor('pomodoro', loadSettings()));
  const [isRunning, setIsRunning] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [draftSettings, setDraftSettings] = useState(settings);
  const [pomodoroCycle, setPomodoroCycle] = useState(() => parseInt(localStorage.getItem('pomodoroCycle') || '0', 10));
  const [tasks, setTasks] = useState(() => JSON.parse(localStorage.getItem('tasks')) || []);
  const [activeTaskId, setActiveTaskId] = useState(loadActiveTaskId);

  const audioRef = useRef(null);
  const endTimeRef = useRef(null);
  const totalSecondsRef = useRef(durationFor('pomodoro', settings));

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Keep the task list (and who's "in focus") in sync with TaskManager, rendered alongside this component.
  useEffect(() => {
    const sync = () => setTasks(JSON.parse(localStorage.getItem('tasks')) || []);
    window.addEventListener('tasks-updated', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('tasks-updated', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  // Timestamp-based ticking avoids drift from setInterval throttling in background tabs.
  useEffect(() => {
    if (!isRunning) return undefined;
    const id = setInterval(() => {
      setSecondsLeft(Math.max(0, Math.round((endTimeRef.current - Date.now()) / 1000)));
    }, 250);
    return () => clearInterval(id);
  }, [isRunning]);

  useEffect(() => {
    if (isRunning && secondsLeft === 0) {
      setIsRunning(false);
      handleSessionComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft, isRunning]);

  useEffect(() => {
    const meta = MODE_META[sessionType];
    document.title = isRunning
      ? `${formatTime(secondsLeft)} — ${meta.short} | Supomodoro`
      : 'Supomodoro — Focus Timer';
    const progress = totalSecondsRef.current > 0 ? 1 - secondsLeft / totalSecondsRef.current : 0;
    updateFavicon(isRunning ? progress : 0, meta.to);
    return () => {
      document.title = 'Supomodoro — Focus Timer';
    };
  }, [secondsLeft, isRunning, sessionType]);

  useEffect(() => {
    const handleKey = (e) => {
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName) || showSettings || showModal) return;
      if (e.code === 'Space') {
        e.preventDefault();
        isRunning ? handlePause() : handleStart();
      } else if (e.key === 'r' || e.key === 'R') {
        handleReset();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  });

  const logSession = (type, durationMinutes, taskId, taskText) => {
    const sessions = JSON.parse(localStorage.getItem('sessions')) || [];
    sessions.push({
      type,
      duration: durationMinutes,
      pomodoros: type === 'pomodoro' ? 1 : 0,
      completedAt: new Date().toISOString(),
      taskId: taskId || null,
      taskText: taskText || null,
    });
    localStorage.setItem('sessions', JSON.stringify(sessions));
  };

  const incrementTaskPomodoro = (taskId) => {
    if (!taskId) return;
    const currentTasks = JSON.parse(localStorage.getItem('tasks')) || [];
    const updated = currentTasks.map((t) =>
      t.id === taskId && t.completedPomodoros < t.estimatedPomodoros
        ? { ...t, completedPomodoros: t.completedPomodoros + 1 }
        : t
    );
    localStorage.setItem('tasks', JSON.stringify(updated));
    setTasks(updated);
    window.dispatchEvent(new Event('tasks-updated'));
  };

  const playAlert = () => {
    if (settings.soundEnabled && audioRef.current) {
      audioRef.current.volume = settings.volume;
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  };

  const notify = (title, body) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, { body });
    }
  };

  const beginSession = (type) => {
    const dur = durationFor(type, settings);
    totalSecondsRef.current = dur;
    setSessionType(type);
    setSecondsLeft(dur);
    endTimeRef.current = Date.now() + dur * 1000;
    setIsRunning(true);
  };

  const handleSessionComplete = () => {
    const durationMinutes = Math.round(totalSecondsRef.current / 60);
    const task = tasks.find((t) => t.id === activeTaskId);
    logSession(sessionType, durationMinutes, activeTaskId, task ? task.text : null);
    playAlert();

    let nextType;
    if (sessionType === 'pomodoro') {
      incrementTaskPomodoro(activeTaskId);
      const nextCycle = pomodoroCycle + 1;
      localStorage.setItem('pomodoroCycle', String(nextCycle));
      setPomodoroCycle(nextCycle);
      nextType = nextCycle % settings.longBreakInterval === 0 ? 'long-break' : 'short-break';
      notify('🍅 Focus session complete!', 'Great work — time for a break.');
      setModalMessage('Pomodoro complete! Time for a break.');
    } else {
      nextType = 'pomodoro';
      notify(sessionType === 'long-break' ? '🌿 Long break over' : '☕ Break over', 'Ready to focus again?');
      setModalMessage(`${MODE_META[sessionType].label} finished!`);
    }
    setShowModal(true);

    const autoStart = sessionType === 'pomodoro' ? settings.autoStartBreaks : settings.autoStartPomodoros;
    if (autoStart) {
      beginSession(nextType);
    } else {
      const dur = durationFor(nextType, settings);
      totalSecondsRef.current = dur;
      setSessionType(nextType);
      setSecondsLeft(dur);
    }
  };

  const handleStart = () => {
    if (secondsLeft <= 0) {
      beginSession(sessionType);
      return;
    }
    endTimeRef.current = Date.now() + secondsLeft * 1000;
    setIsRunning(true);
  };

  const handlePause = () => setIsRunning(false);

  const handleReset = () => {
    setIsRunning(false);
    const dur = durationFor(sessionType, settings);
    totalSecondsRef.current = dur;
    setSecondsLeft(dur);
  };

  const handleTaskChange = (e) => {
    const val = e.target.value === '' ? null : e.target.value;
    setActiveTaskId(val);
    localStorage.setItem('activeTaskId', JSON.stringify(val));
    window.dispatchEvent(new Event('tasks-updated'));
  };

  const handleSaveSettings = () => {
    const cleaned = {
      pomodoro: Math.max(1, parseInt(draftSettings.pomodoro, 10) || DEFAULT_SETTINGS.pomodoro),
      shortBreak: Math.max(1, parseInt(draftSettings.shortBreak, 10) || DEFAULT_SETTINGS.shortBreak),
      longBreak: Math.max(1, parseInt(draftSettings.longBreak, 10) || DEFAULT_SETTINGS.longBreak),
      longBreakInterval: Math.max(1, parseInt(draftSettings.longBreakInterval, 10) || DEFAULT_SETTINGS.longBreakInterval),
      autoStartBreaks: !!draftSettings.autoStartBreaks,
      autoStartPomodoros: !!draftSettings.autoStartPomodoros,
      soundEnabled: !!draftSettings.soundEnabled,
      volume: draftSettings.volume,
    };
    localStorage.setItem('pomodoroSettings', JSON.stringify(cleaned));
    setSettings(cleaned);
    if (!isRunning) {
      const dur = durationFor(sessionType, cleaned);
      totalSecondsRef.current = dur;
      setSecondsLeft(dur);
    }
    setShowSettings(false);
  };

  const meta = MODE_META[sessionType];
  const progress = totalSecondsRef.current > 0 ? 1 - secondsLeft / totalSecondsRef.current : 0;
  const activeTask = tasks.find((t) => t.id === activeTaskId);
  const incompleteTasks = tasks.filter((t) => !t.completed);

  return (
    <Container maxWidth="sm" className={`timer-container ${sessionType}`}>
      {/* Mode tabs */}
      <Box display="flex" justifyContent="center" gap={1.5} mb={3}>
        {Object.keys(MODE_META).map((type) => (
          <Button
            key={type}
            size="small"
            variant={sessionType === type ? 'contained' : 'outlined'}
            onClick={() => beginSession(type)}
            sx={
              sessionType === type
                ? gradientSx(MODE_META[type].from, MODE_META[type].to, { flex: 'unset' })
                : { flex: 'unset' }
            }
          >
            {MODE_META[type].emoji} {MODE_META[type].short}
          </Button>
        ))}
      </Box>

      {/* Circular progress ring */}
      <Box
        sx={{
          position: 'relative',
          width: { xs: 220, sm: 260 },
          height: { xs: 220, sm: 260 },
          mx: 'auto',
          mb: 3,
        }}
      >
        <svg width="100%" height="100%" viewBox="0 0 260 260">
          <circle cx="130" cy="130" r={RADIUS} fill="none" stroke="rgba(128,128,128,0.15)" strokeWidth="14" />
          <circle
            cx="130"
            cy="130"
            r={RADIUS}
            fill="none"
            stroke={meta.to}
            strokeWidth="14"
            strokeLinecap="round"
            strokeDasharray={CIRC}
            strokeDashoffset={CIRC * (1 - progress)}
            transform="rotate(-90 130 130)"
            style={{ transition: 'stroke-dashoffset 0.3s linear' }}
          />
        </svg>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              fontFamily: "'Montserrat', sans-serif",
              fontVariantNumeric: 'tabular-nums',
              fontSize: { xs: '2.4rem', sm: '3rem' },
            }}
          >
            {formatTime(secondsLeft)}
          </Typography>
          <Typography variant="overline" sx={{ letterSpacing: 2, opacity: 0.7 }}>
            {meta.emoji} {meta.label}
          </Typography>
        </Box>
      </Box>

      {/* Start / Pause / Reset */}
      <Box display="flex" justifyContent="center" gap={2} mb={2}>
        {isRunning ? (
          <Button variant="contained" sx={gradientSx('#FF3D00', '#D50000')} onClick={handlePause} startIcon={<Pause />}>
            Pause
          </Button>
        ) : (
          <Button variant="contained" sx={gradientSx('#FF4081', '#F50057')} onClick={handleStart} startIcon={<PlayArrow />}>
            Start
          </Button>
        )}
        <Button variant="contained" sx={gradientSx('#1DE9B6', '#00BFAE')} onClick={handleReset} startIcon={<Refresh />}>
          Reset
        </Button>
        <IconButton
          onClick={() => {
            setDraftSettings(settings);
            setShowSettings(true);
          }}
          sx={{ border: '1px solid rgba(128,128,128,0.3)' }}
        >
          <SettingsIcon />
        </IconButton>
      </Box>

      {/* Focus task selector */}
      <FormControl fullWidth size="small" sx={{ mt: 2, mb: 1 }}>
        <InputLabel id="focus-task-label">Focusing on</InputLabel>
        <Select
          labelId="focus-task-label"
          value={activeTaskId ?? ''}
          label="Focusing on"
          onChange={handleTaskChange}
        >
          <MenuItem value="">No task selected</MenuItem>
          {incompleteTasks.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.text} ({t.completedPomodoros}/{t.estimatedPomodoros} 🍅)
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {activeTask && (
        <Typography variant="caption" align="center" display="block" color="textSecondary" sx={{ mb: 1 }}>
          Completing this pomodoro will count toward "{activeTask.text}"
        </Typography>
      )}

      <Typography variant="caption" align="center" display="block" color="textSecondary" sx={{ opacity: 0.6 }}>
        Space to start/pause · R to reset
      </Typography>

      {/* Session complete dialog */}
      <Dialog open={showModal} onClose={() => setShowModal(false)}>
        <DialogTitle align="center">{modalMessage}</DialogTitle>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button variant="contained" sx={gradientSx('#FB8C00', '#FFEB3B')} onClick={() => setShowModal(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settings dialog */}
      <Dialog open={showSettings} onClose={() => setShowSettings(false)} maxWidth="xs" fullWidth>
        <DialogTitle>⚙️ Timer Settings</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Pomodoro (minutes)"
              type="number"
              value={draftSettings.pomodoro}
              onChange={(e) => setDraftSettings((p) => ({ ...p, pomodoro: e.target.value }))}
              inputProps={{ min: 1 }}
              fullWidth
            />
            <TextField
              label="Short Break (minutes)"
              type="number"
              value={draftSettings.shortBreak}
              onChange={(e) => setDraftSettings((p) => ({ ...p, shortBreak: e.target.value }))}
              inputProps={{ min: 1 }}
              fullWidth
            />
            <TextField
              label="Long Break (minutes)"
              type="number"
              value={draftSettings.longBreak}
              onChange={(e) => setDraftSettings((p) => ({ ...p, longBreak: e.target.value }))}
              inputProps={{ min: 1 }}
              fullWidth
            />
            <TextField
              label="Pomodoros until long break"
              type="number"
              value={draftSettings.longBreakInterval}
              onChange={(e) => setDraftSettings((p) => ({ ...p, longBreakInterval: e.target.value }))}
              inputProps={{ min: 1 }}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={draftSettings.autoStartBreaks}
                  onChange={(e) => setDraftSettings((p) => ({ ...p, autoStartBreaks: e.target.checked }))}
                />
              }
              label="Auto-start breaks"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={draftSettings.autoStartPomodoros}
                  onChange={(e) => setDraftSettings((p) => ({ ...p, autoStartPomodoros: e.target.checked }))}
                />
              }
              label="Auto-start next pomodoro"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={draftSettings.soundEnabled}
                  onChange={(e) => setDraftSettings((p) => ({ ...p, soundEnabled: e.target.checked }))}
                />
              }
              label="Alarm sound"
            />
            <Box display="flex" alignItems="center" gap={2}>
              {draftSettings.soundEnabled ? <VolumeUp /> : <VolumeOff />}
              <Slider
                value={draftSettings.volume}
                min={0}
                max={1}
                step={0.05}
                disabled={!draftSettings.soundEnabled}
                onChange={(_, val) => setDraftSettings((p) => ({ ...p, volume: val }))}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSettings(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSettings}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <audio ref={audioRef} src="/alert.mp3" />
    </Container>
  );
};

export default Timer;
